import { Link } from "react-router-dom";
import { useState } from "react";
import { Construction, Lock, Globe, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoginDialog } from "@/components/auth/LoginDialog";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";

const ComingSoon = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [loginOpen, setLoginOpen] = useState(false);

  const handleInternerBereich = () => {
    if (user) {
      // Bereits eingeloggt - direkt zum Dashboard
      window.location.href = '/dashboard';
    } else {
      // Nicht eingeloggt - Login-Dialog öffnen
      setLoginOpen(true);
    }
  };

  return (
    <div className="dark h-screen bg-black text-foreground flex flex-col relative overflow-hidden">
      {/* Background gradient - NexaBoard colors (blue to purple) */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 h-[600px] w-[800px] rounded-full bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-600/20 blur-[120px]" />
        <div className="absolute right-1/4 top-2/3 h-[300px] w-[400px] rounded-full bg-purple-500/15 blur-[80px]" />
        <div className="absolute left-1/4 top-1/4 h-[250px] w-[350px] rounded-full bg-cyan-400/10 blur-[100px]" />
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        {/* Logo */}
        <img
          alt="NexaBoard Logo"
          className="h-20 md:h-48 lg:h-56 mb-6 md:mb-8 object-contain"
          src="/lovable-uploads/nexaboard-logo-600.webp"
          width={600}
          height={224}
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />

        {/* Construction Icon */}
        <div className="mb-4 p-3 rounded-full bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-600/10 border border-blue-500/20">
          <Construction className="h-8 w-8 md:h-10 md:w-10 text-blue-400" />
        </div>

        {/* Headline */}
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
          Seite befindet sich im{" "}
          <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            Aufbau
          </span>
        </h1>

        {/* Description */}
        <p className="text-muted-foreground text-base md:text-lg max-w-xl mb-4">
          Wir arbeiten fleißig daran, Ihnen bald unsere neuen digitalen Lösungen
          präsentieren zu können. Schauen Sie bald wieder vorbei!
        </p>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <div className="h-2 w-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 animate-pulse" />
          <span>In Entwicklung</span>
        </div>
      </main>

      {/* Footer */}
      <footer className="shrink-0 py-4 px-6 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 max-w-6xl mx-auto">
          {/* Links */}
          <div className="flex items-center gap-4 text-sm">
            <Link
              to="/impressum"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Impressum
            </Link>
            <span className="text-muted-foreground/50">|</span>
            <Link
              to="/datenschutz"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Datenschutz
            </Link>
            {user && isAdmin && (
              <>
                <span className="text-muted-foreground/50">|</span>
                <Link
                  to="/startseite"
                  className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
                >
                  <Globe className="h-3 w-3" />
                  Startseite
                </Link>
              </>
            )}
          </div>

          {/* Copyright */}
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Kalidio UG (haftungsbeschränkt). Alle Rechte vorbehalten.
          </p>

          <div className="flex items-center gap-2">
            {/* Admin-Zugang Button - nur für Admins */}
            {user && isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.href = '/admin'}
                className="border-red-500/40 bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-400 hover:border-red-500/60 hover:from-red-500/20 hover:to-orange-500/20 hover:text-red-300 transition-all duration-300 gap-2 backdrop-blur-sm"
              >
                <Shield className="h-3.5 w-3.5" />
                <span className="text-sm font-medium">Admin-Zugang</span>
              </Button>
            )}

            {/* Interner Bereich Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleInternerBereich}
              className="border-blue-500/40 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-600/10 text-blue-400 hover:border-purple-500/60 hover:from-cyan-500/20 hover:via-blue-500/20 hover:to-purple-600/20 hover:text-purple-300 transition-all duration-300 gap-2 backdrop-blur-sm"
            >
              <Lock className="h-3.5 w-3.5" />
              <span className="text-sm font-medium">
                {user ? "Zum Dashboard" : "Interner Bereich"}
              </span>
            </Button>
          </div>
        </div>
      </footer>

      {/* Login Dialog */}
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
};

export default ComingSoon;
